import { useCallback, useEffect, useState } from 'react';

import { LocalStorageService, StorageDefaults } from '~/services/localStorage';

/**
 * Custom hook to manage local storage with React state synchronization.
 *
 * Updates other hooks on the same page by listening to the 'storage' event.
 * Manually emits a 'storage' event on set to ensure same-page updates.
 *
 * @param key The key in local storage to manage.
 * @param defaultValue The default value to use if the key is not found.
 * @returns A tuple with the current value and a setter function.
 */
export function useLocalStorage<K extends keyof typeof StorageDefaults>(
  key: K,
  defaultValue?: (typeof StorageDefaults)[K]
) {
  const [value, setValue] = useState<(typeof StorageDefaults)[K]>(() => {
    const stored = LocalStorageService.getItem(key);
    return stored !== null ? stored : (defaultValue ?? StorageDefaults[key]);
  });

  const setStoredValue = useCallback(
    (
      newValue:
        | (typeof StorageDefaults)[K]
        | ((prev: (typeof StorageDefaults)[K]) => (typeof StorageDefaults)[K])
    ) => {
      const valueToStore =
        newValue instanceof Function ? newValue(value) : newValue;
      setValue(valueToStore);
      LocalStorageService.setItem(key, valueToStore);

      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          oldValue: JSON.stringify(value),
          newValue: JSON.stringify(valueToStore),
          storageArea: window.localStorage,
          url: window.location.href,
        })
      );
    },
    [key, value]
  );

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === key &&
        e.newValue !== null &&
        e.newValue !== JSON.stringify(value)
      ) {
        try {
          const newValue = JSON.parse(e.newValue);
          setValue(newValue);
        } catch {
          setValue(e.newValue as unknown as (typeof StorageDefaults)[K]);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [value, setStoredValue] as const;
}
